import { useCallback, useEffect, useState } from 'react';
import { Money } from '@delivery/shared';
import { ApiFailure, api } from '../lib/api';
import { money } from '../lib/format';

interface Row {
  id: string;
  postcodeFrom: string;
  postcodeTo: string;
  mode: Mode;
  price: string;
  includeExtras: boolean;
  label: string | null;
}

const INPUT =
  'w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1.5 text-sm';

type Mode = 'FIXED' | 'REMOTE';
const MODE_LABEL: Record<Mode, string> = { FIXED: '具体运费', REMOTE: '偏远加价' };

const blank = { postcodeFrom: '', postcodeTo: '', mode: 'FIXED' as Mode, price: '', label: '', includeExtras: false };

/**
 * 邮编运费:「什么邮编到什么邮编,收多少运费 / 算偏远、加多少」。
 *
 * ★ 底层本来也配得出来(建区 → 给区挂邮编区间 → 给区设价),但那是三步,
 *   而运营脑子里的价目表就是一张「邮编段 → 价钱」的表。
 *
 * ★ 两种怎么算要在这里写明白:具体运费就收这个数;偏远加价是照常算完再加。
 */
export function PostcodePrices({ canManage }: { canManage: boolean }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [form, setForm] = useState(blank);
  /** 正在改哪一条;null = 新加 */
  const [editingId, setEditingId] = useState<string | null>(null);
  /** 删除要点两下:第一下变成「确认删除」 */
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await api<Row[]>('/pricing/postcode-prices'));
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '读取邮编运费失败');
    }
  }, []);
  useEffect(() => void load(), [load]);

  const price = Money.tryFromMajor(form.price);
  const canSave = form.postcodeFrom.trim() !== '' && form.price.trim() !== '' && price != null && !busy;

  async function save() {
    if (price == null) return;
    setBusy(true);
    setErr(null);
    try {
      await api(editingId ? `/pricing/postcode-prices/${editingId}` : '/pricing/postcode-prices', {
        method: editingId ? 'PUT' : 'POST',
        body: {
          postcodeFrom: form.postcodeFrom.trim(),
          postcodeTo: form.postcodeTo.trim() || null,
          mode: form.mode,
          price,
          includeExtras: form.mode === 'FIXED' && form.includeExtras,
          label: form.label.trim() || null,
        },
      });
      setForm(blank);
      setEditingId(null);
      await load();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '没存上');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setBusy(true);
    setErr(null);
    try {
      await api(`/pricing/postcode-prices/${id}`, { method: 'DELETE' });
      setConfirmDel(null);
      if (editingId === id) {
        setEditingId(null);
        setForm(blank);
      }
      await load();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '没删掉');
    } finally {
      setBusy(false);
    }
  }

  function edit(r: Row) {
    setEditingId(r.id);
    setConfirmDel(null);
    setForm({
      postcodeFrom: r.postcodeFrom,
      postcodeTo: r.postcodeTo === r.postcodeFrom ? '' : r.postcodeTo,
      mode: r.mode,
      price: Money.toMajorString(Money.minor(Number(r.price))),
      label: r.label ?? '',
      includeExtras: r.includeExtras,
    });
  }

  return (
    <section
      aria-label="邮编运费"
      className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
    >
      <div className="mb-1 font-medium text-slate-800 dark:text-slate-200">邮编运费</div>
      <p className="mb-3 text-xs text-slate-500">
        收件邮编落在区间里时:<b>具体运费</b>就收这个数,优先于区域价(商家专属价不受影响),区域偏远加价不再叠,
        超重、多件、代收手续费默认不另加,勾上「另加」才按计价规则加;<b>偏远加价</b>是运费照常算,再加这个数(替掉区域上的偏远加价)。
        没填邮编的单从地址末尾取(如 Auckland 1010)。
        同一个邮编里有几片价格不一样的话:在「设置 → 配送区域」里把那一片画出来、给那个区单独设价 ——
        收货点落在画的范围里就按那个区的价,不按邮编价。
      </p>

      {err && <div className="mb-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="text-slate-500">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">邮编</th>
              <th className="px-2 py-1.5 text-left font-medium">备注</th>
              <th className="px-2 py-1.5 text-left font-medium">类型</th>
              <th className="px-2 py-1.5 text-left font-medium">金额</th>
              <th className="px-2 py-1.5 text-left font-medium">超重 / 多件 / 代收</th>
              <th className="px-2 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-2 py-4 text-center text-slate-400">
                  还没设 —— 所有单按区域价 / 计价规则算
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.id}
                className={`border-t border-slate-100 dark:border-slate-700/60 ${
                  editingId === r.id ? 'bg-brand-50 dark:bg-brand-500/10' : ''
                }`}
              >
                <td className="px-2 py-1.5 font-mono">
                  {r.postcodeFrom === r.postcodeTo ? r.postcodeFrom : `${r.postcodeFrom} ~ ${r.postcodeTo}`}
                </td>
                <td className="px-2 py-1.5 text-slate-600 dark:text-slate-300">{r.label ?? ''}</td>
                <td className="px-2 py-1.5">
                  <span
                    className={`rounded px-1.5 py-0.5 text-xs ${
                      r.mode === 'REMOTE'
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300'
                        : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
                    }`}
                  >
                    {MODE_LABEL[r.mode]}
                  </span>
                </td>
                <td className="px-2 py-1.5 font-mono">
                  {r.mode === 'REMOTE' ? '+' : ''}
                  {money(r.price)}
                </td>
                <td className="px-2 py-1.5 text-xs text-slate-500">
                  {r.mode === 'REMOTE' ? '照规则算' : r.includeExtras ? '另加' : '不加'}
                </td>
                <td className="whitespace-nowrap px-2 py-1.5 text-right">
                  {canManage && (
                    <>
                      <button onClick={() => edit(r)} className="mr-3 text-brand-600 hover:underline">
                        编辑
                      </button>
                      {confirmDel === r.id ? (
                        <button
                          onClick={() => void remove(r.id)}
                          disabled={busy}
                          className="font-medium text-red-600 hover:underline"
                        >
                          确认删除
                        </button>
                      ) : (
                        <button onClick={() => setConfirmDel(r.id)} className="text-red-600 hover:underline">
                          删除
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canManage && (
        <div
          role="group"
          aria-label={editingId ? '修改邮编运费' : '新加邮编运费'}
          className="mt-3 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3 sm:grid-cols-[1fr_1fr_1.1fr_1fr_1.4fr_auto] sm:items-end dark:border-slate-700/60"
        >
          <label className="block text-sm">
            <span className="text-slate-500">起始邮编</span>
            <input
              inputMode="numeric"
              value={form.postcodeFrom}
              onChange={(e) => setForm({ ...form, postcodeFrom: e.target.value })}
              placeholder="1010"
              className={`mt-1 ${INPUT}`}
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-500">结束邮编</span>
            <input
              inputMode="numeric"
              value={form.postcodeTo}
              onChange={(e) => setForm({ ...form, postcodeTo: e.target.value })}
              placeholder="不填 = 只这一个"
              className={`mt-1 ${INPUT}`}
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-500">类型</span>
            <select
              value={form.mode}
              onChange={(e) => setForm({ ...form, mode: e.target.value as Mode })}
              className={`mt-1 ${INPUT}`}
            >
              <option value="FIXED">具体运费</option>
              <option value="REMOTE">偏远加价</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-slate-500">{form.mode === 'REMOTE' ? '加价(元)' : '运费(元)'}</span>
            <input
              inputMode="decimal"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              placeholder="8.00"
              className={`mt-1 ${INPUT}`}
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-500">备注</span>
            <input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="比如:市中心"
              className={`mt-1 ${INPUT}`}
            />
          </label>
          <div className="col-span-2 flex gap-2 sm:col-span-1">
            <button
              onClick={() => void save()}
              disabled={!canSave}
              className="h-9 flex-1 whitespace-nowrap rounded-lg bg-brand-600 px-4 text-sm text-white hover:bg-brand-700 disabled:opacity-40"
            >
              {editingId ? '保存修改' : '添加'}
            </button>
            {editingId && (
              <button
                onClick={() => {
                  setEditingId(null);
                  setForm(blank);
                }}
                className="h-9 rounded-lg border border-slate-300 px-3 text-sm dark:border-slate-600"
              >
                取消
              </button>
            )}
          </div>
          {form.mode === 'FIXED' ? (
            <label className="col-span-2 flex items-center gap-2 text-sm text-slate-600 sm:col-span-6 dark:text-slate-300">
              <input
                type="checkbox"
                checked={form.includeExtras}
                onChange={(e) => setForm({ ...form, includeExtras: e.target.checked })}
              />
              超重、多件、代收手续费照计价规则另加(不勾 = 就收这个运费)
            </label>
          ) : (
            <p className="col-span-2 text-xs text-slate-500 sm:col-span-6">
              这段邮编按偏远地区算:运费照计价规则算,再加上面这个数(替掉区域上的偏远加价,不会加两次)
            </p>
          )}
        </div>
      )}
    </section>
  );
}
